import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * 🔥 Phase 3: Prefetch des pages fréquentes après login
 * Précharge les données de insights et opportunities en arrière-plan
 */
export const AppPrefetch = ({ children }: { children: React.ReactNode }) => {
  const { user, isInitialized } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && isInitialized) {
      // ✅ Précharger insights (5 min de cache)
      queryClient.prefetchQuery({
        queryKey: ['insights', {}],
        queryFn: async () => {
          const { data } = await supabase
            .from('tech_insights')
            .select('*')
            .eq('status', 'published')
            .order('publication_date', { ascending: false, nullsFirst: false })
            .order('created_at', { ascending: false });
          return data || [];
        },
        staleTime: 5 * 60 * 1000,
      });

      // ✅ Précharger opportunities (5 min de cache)
      queryClient.prefetchQuery({
        queryKey: ['opportunities', {}],
        queryFn: async () => {
          const { data } = await supabase
            .from('opportunities')
            .select('*')
            .in('status', ['active', 'pending', 'closed', 'archived'])
            .order('created_at', { ascending: false });
          return data || [];
        },
        staleTime: 5 * 60 * 1000,
      });

      // ✅ Précharger bookmarks
      queryClient.prefetchQuery({
        queryKey: ['insight-bookmarks'],
        queryFn: async () => {
          const { data } = await supabase
            .from('insight_bookmarks')
            .select('id, insight_id, user_id, created_at')
            .order('created_at', { ascending: false });
          return data || [];
        },
        staleTime: 5 * 60 * 1000,
      });

      console.log('✅ [Prefetch] Données insights et opportunities préchargées');
    }
  }, [user, isInitialized, queryClient]);

  return <>{children}</>;
};
